import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { CalendarInput } from '../../../components/ui/calendar-input';
import { WorkflowHeader } from '../../../components/shared/WorkflowHeader';
import { AutoTaskPromptModal } from '../../../components/shared/AutoTaskPromptModal';
import { TasksSection } from '../../../components/shared/TasksSection';
import { YesNoButtonGroup, CheckboxGrid } from '../../../components/workflow';
import { dsrV2API, entitiesAPI, exportAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  ArrowLeft, ArrowRight, Save, Building2,
  User, Shield, Search, FileText, Send, ClipboardCheck,
  Timer, XCircle, Users, CheckCircle2, FileDown
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { format } from 'date-fns';
import { FormStepper, StepperProgress } from '../../../components/ui/form-stepper';
import {
  REQUEST_TYPES,
  RECEIVED_VIA_OPTIONS,
  VERIFICATION_METHODS,
  VERIFICATION_GUIDANCE,
  EXEMPTION_TYPES,
  EXEMPTION_GUIDANCE,
  DECISION_OPTIONS,
  RESPONSE_METHODS,
  STATUS_OPTIONS,
  DEPARTMENTS,
  AGENT_RELATIONSHIPS,
  EXTENSION_REASONS,
  DSR_INITIAL_FORM_DATA,
  JURISDICTION_OPTIONS,
} from './constants';

// Tab configuration with icons
const TABS = [
  { id: 'request-info', label: 'Request Info', icon: FileText },
  { id: 'requester', label: 'Requester', icon: User },
  { id: 'verification', label: 'Verification', icon: Shield },
  { id: 'data-search', label: 'Data Search', icon: Search },
  { id: 'response', label: 'Response', icon: Send },
  { id: 'review', label: 'Review', icon: ClipboardCheck },
];

// Use the imported initial form data (from constants.js)
const initialFormData = DSR_INITIAL_FORM_DATA;

export default function DSRWorkflowPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isDPO, user, isComplianceReadOnly: isReadOnly } = useAuth();
  const { theme } = useTheme();
  const isEditing = !!id;

  const [formData, setFormData] = useState(initialFormData);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [taskPromptOpen, setTaskPromptOpen] = useState(false);
  const [taskPromptDsrId, setTaskPromptDsrId] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const [activeTab, setActiveTab] = useState('request-info');
  const [recordId, setRecordId] = useState(id || null);
  const [dsrReference, setDsrReference] = useState('');

  const lastSavedData = useRef(null);
  const autoSaveTimer = useRef(null);

  // Calculate progress
  const calculateProgress = useCallback(() => {
    let total = 0;
    let completed = 0;

    // Request Info
    total += 4;
    if (formData.entity_id || !isDPO) completed++;
    if (formData.request_type) completed++;
    if (formData.date_received) completed++;
    if (formData.request_summary) completed++;

    // Requester
    total += 2;
    if (formData.requester.name) completed++;
    if (formData.requester.email) completed++;

    // Verification
    total += 1;
    if (formData.identity_verification.verified !== null) completed++;

    // Data Search
    total += 1;
    if (formData.data_search.completed !== null) completed++;

    // Response
    total += 1;
    if (formData.response_info.decision) completed++;

    return Math.round((completed / total) * 100);
  }, [formData, isDPO]);

  const progressPercentage = calculateProgress();

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (isDPO) {
          const entitiesRes = await entitiesAPI.getAll();
          setEntities(entitiesRes.data);
        }

        if (isEditing) {
          const dsrRes = await dsrV2API.getOne(id);
          const dsr = dsrRes.data;

          // Map backend field names to frontend field names
          const requesterData = dsr.requester ? {
            ...initialFormData.requester,
            ...dsr.requester,
            is_agent: dsr.requester.is_third_party, // Backend is_third_party → Frontend is_agent
            agent_name: dsr.requester.third_party_name, // Backend third_party_name → Frontend agent_name
            agent_relationship: dsr.requester.third_party_relationship, // Backend third_party_relationship → Frontend agent_relationship
            agent_authorization: !!dsr.requester.third_party_authorization, // Backend string → Frontend boolean (true if "obtained", false if null)
          } : initialFormData.requester;

          setFormData({
            ...initialFormData,
            ...dsr,
            entity_id: dsr.entity_id || dsr.organization_id,
            requester: requesterData,
            identity_verification: { ...initialFormData.identity_verification, ...(dsr.verification || dsr.identity_verification) },
            deadline_info: { ...initialFormData.deadline_info, ...dsr.deadline_info },
            data_search: { ...initialFormData.data_search, ...dsr.data_search },
            response_info: { ...initialFormData.response_info, ...(dsr.response || dsr.response_info) },
          });
          setDsrReference(dsr.dsr_reference || '');
          lastSavedData.current = JSON.stringify(dsr);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditing, isDPO]);

  // Auto-save
  const performAutoSave = useCallback(async () => {
    if (!formData.requester.name) return;
    if (!formData.entity_id && isDPO) return;

    const currentData = JSON.stringify(formData);
    if (currentData === lastSavedData.current) return;

    try {
      setAutoSaveStatus('saving');

      const submitData = {
        ...formData,
        entity_id: formData.entity_id || user?.organization_id,
        requester: {
          ...formData.requester,
          is_third_party: formData.requester.is_agent,
          third_party_name: formData.requester.agent_name,
          third_party_relationship: formData.requester.agent_relationship,
          third_party_authorization: formData.requester.agent_authorization ? "obtained" : null,
        },
        verification: {
          ...formData.identity_verification,
          third_party_identity_verified: formData.identity_verification?.third_party_identity_verified || false,
        },
      };
      delete submitData.identity_verification; // Remove frontend field name, use backend field name

      if (recordId) {
        await dsrV2API.update(recordId, submitData);
      } else {
        const res = await dsrV2API.create(submitData);
        setRecordId(res.data.id);
        setDsrReference(res.data.dsr_reference);
        window.history.replaceState(null, '', `/compliance/dsr/workflow/${res.data.id}`);
      }

      lastSavedData.current = currentData;
      setAutoSaveStatus('saved');

      setTimeout(() => setAutoSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveStatus('idle');
    }
  }, [formData, recordId, isDPO, user]);

  useEffect(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(performAutoSave, 2000);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [formData, performAutoSave]);

  // Handle field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const toggleArrayItem = (parent, field, item) => {
    setFormData(prev => {
      const current = prev[parent]?.[field] || [];
      if (current.includes(item)) {
        return {
          ...prev,
          [parent]: { ...prev[parent], [field]: current.filter(i => i !== item) }
        };
      }
      return {
        ...prev,
        [parent]: { ...prev[parent], [field]: [...current, item] }
      };
    });
  };

  // Handle extension notification sent - triggers activity log entry
  const handleExtensionNotificationChange = async (value) => {
    handleNestedChange('deadline_info', 'extension_notification_sent', value);

    // If setting to true and DSR exists, add activity log entry
    if (value && recordId) {
      try {
        await dsrV2API.addActivity(recordId, 'Extension notification sent to data subject');
        toast.success('Activity recorded');
      } catch (error) {
        console.error('Failed to record activity:', error);
        toast.error('Failed to record activity');
      }
    }
  };

  // Navigation
  const goToNextTab = () => {
    const currentIndex = TABS.findIndex(t => t.id === activeTab);
    if (currentIndex < TABS.length - 1) {
      setActiveTab(TABS[currentIndex + 1].id);
    }
  };

  const goToPrevTab = () => {
    const currentIndex = TABS.findIndex(t => t.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(TABS[currentIndex - 1].id);
    }
  };

  // Final save
  const handleSave = async (asDraft = false) => {
    // Validate: Cannot save refusal/partial decisions without appeal rights confirmed
    if (!asDraft) {
      const isRefusalDecision = formData.response_info.decision?.startsWith('refused_') || formData.response_info.decision === 'granted_partial';

      if (isRefusalDecision && !formData.response_info.appeal_rights_communicated) {
        toast.error('Cannot save a refusal or partial decision without confirming appeal rights have been communicated. Return to Step 5 — Response.');
        return;
      }
    }

    try {
      setSaving(true);

      const submitData = {
        ...formData,
        entity_id: formData.entity_id || user?.organization_id,
        status: asDraft ? formData.status : (formData.response_info.decision ? 'responded' : formData.status),
        requester: {
          ...formData.requester,
          is_third_party: formData.requester.is_agent,
          third_party_name: formData.requester.agent_name,
          third_party_relationship: formData.requester.agent_relationship,
          third_party_authorization: formData.requester.agent_authorization ? "obtained" : null,
        },
        verification: {
          ...formData.identity_verification,
          third_party_identity_verified: formData.identity_verification?.third_party_identity_verified || false,
        },
      };
      delete submitData.identity_verification; // Remove frontend field name, use backend field name

      let finalId = recordId;
      if (recordId) {
        await dsrV2API.update(recordId, submitData);
        toast.success(asDraft ? 'DSR saved as draft' : 'DSR saved successfully');
      } else {
        const res = await dsrV2API.create(submitData);
        finalId = res.data.id;
        setRecordId(finalId);
        toast.success('DSR created successfully');
      }

      // Spec Section 5: offer auto-generated DSR response task on final (non-draft) save (DPO only)
      if (!asDraft && isDPO && finalId) {
        setTaskPromptDsrId(finalId);
        setTaskPromptOpen(true);
      } else if (!asDraft) {
        navigate('/compliance/dsr');
      }
    } catch (error) {
      console.error('Save failed:', error);
      let errorMsg = 'Failed to save';
      const detail = error.response?.data?.detail;

      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => (typeof e === 'string' ? e : e.msg || 'Validation error')).join(', ');
      } else if (detail?.msg) {
        errorMsg = detail.msg;
      }

      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isMinor = formData.requester?.is_minor;
  const isThirdParty = formData.requester?.is_agent;

  return (
    <>
    <AutoTaskPromptModal
      open={taskPromptOpen}
      onOpenChange={(open) => {
        setTaskPromptOpen(open);
        if (!open) navigate('/compliance/dsr');
      }}
      sourceModule="dsr"
      sourceId={taskPromptDsrId}
    />
    <div className={cn("min-h-screen bg-background", theme === 'dark' ? 'dark' : '')}>
      {/* Header */}
      <WorkflowHeader
        title={isEditing ? 'Edit DSR' : 'Log New DSR'}
        subtitle="Data Subject Request"
        reference={dsrReference}
        onBack={() => navigate('/compliance/dsr')}
        progressPercentage={progressPercentage}
        autoSaveStatus={autoSaveStatus}
      />

      {/* Flag Badges - Minor and Third-Party */}
      {(isMinor || isThirdParty) && (
        <div className="border-b bg-muted/30 px-6 py-3">
          <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-2">
            {isMinor && (
              <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 gap-1.5">
                <span className="text-sm">👤</span>
                Minor Data Subject
              </Badge>
            )}
            {isThirdParty && (
              <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 gap-1.5">
                <span className="text-sm">👥</span>
                Third Party Request
              </Badge>
            )}
            {isMinor && isThirdParty && (
              <span className="text-xs text-muted-foreground">Both flags apply. This is a high-scrutiny request.</span>
            )}
          </div>
        </div>
      )}

      {/* Main Content with Sidebar Stepper */}
      <div className="flex">
        {/* Left Sidebar - Stepper */}
        <div className="hidden lg:block w-64 border-r bg-muted/30 p-4 sticky top-[73px] h-[calc(100vh-73px)] overflow-y-auto">
          <div className="mb-6">
            <StepperProgress current={TABS.findIndex(t => t.id === activeTab) + 1} total={TABS.length} />
          </div>
          <FormStepper
            steps={TABS.map((tab, index) => ({
              id: tab.id,
              label: tab.label,
              icon: tab.icon
            }))}
            currentStep={TABS.findIndex(t => t.id === activeTab) + 1}
            onStepClick={(step) => setActiveTab(TABS[step - 1].id)}
            completedSteps={TABS.findIndex(t => t.id === activeTab)}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            {/* Mobile Tab Navigation */}
            <TabsList className="lg:hidden grid grid-cols-6 h-auto p-1">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="flex flex-col gap-1 py-2 px-1 text-xs"
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {/* Tab 1: Request Info */}
            <TabsContent value="request-info" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    Request Information
                  </CardTitle>
                  <CardDescription>
                    Record the details of the data subject request
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Jurisdiction — first field, drives deadline calculation and reference number */}
                  <div className="space-y-2">
                    <Label>Jurisdiction <span className="text-red-500">*</span></Label>
                    <Select
                      value={formData.jurisdiction || 'JM'}
                      onValueChange={(val) => handleChange('jurisdiction', val)}
                    >
                      <SelectTrigger data-testid="jurisdiction-select">
                        <SelectValue placeholder="Select jurisdiction" />
                      </SelectTrigger>
                      <SelectContent>
                        {JURISDICTION_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Sets the applicable data protection law, statutory deadline, and DSR reference number.
                    </p>
                  </div>

                  {isDPO && (
                    <div className="space-y-2">
                      <Label>Entity <span className="text-red-500">*</span></Label>
                      <Select
                        value={formData.entity_id || "select"}
                        onValueChange={(val) => handleChange('entity_id', val === "select" ? "" : val)}
                      >
                        <SelectTrigger data-testid="entity-select">
                          <SelectValue placeholder="Select entity" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="select">Select entity</SelectItem>
                          {entities.map(entity => (
                            <SelectItem key={entity.id} value={entity.id}>
                              {entity.entity_name} {entity.client_name ? `(${entity.client_name})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date Received <span className="text-red-500">*</span></Label>
                      <CalendarInput
                        value={formData.date_received?.split('T')[0] || ''}
                        onChange={(date) => handleChange('date_received', date)}
                        placeholder="Select date received"
                        data-testid="date-received-input"
                      />
                  </div>
                  <div className="space-y-2">
                    <Label>Received Via</Label>
                    <Select
                      value={formData.received_via}
                      onValueChange={(val) => handleChange('received_via', val)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RECEIVED_VIA_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Request Type <span className="text-red-500">*</span></Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {REQUEST_TYPES.map(type => (
                      <div
                        key={type.value}
                        className={cn(
                          "p-3 border rounded-lg cursor-pointer transition-colors",
                          formData.request_type === type.value
                            ? "border-primary bg-primary/5"
                            : "hover:border-muted-foreground/50"
                        )}
                        onClick={() => handleChange('request_type', type.value)}
                      >
                        <p className="font-medium text-sm">{type.label}</p>
                        <p className="text-xs text-muted-foreground">{type.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Request Summary <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={formData.request_summary}
                    onChange={(e) => handleChange('request_summary', e.target.value)}
                    placeholder="Brief summary of what the data subject is requesting..."
                    rows={3}
                    data-testid="request-summary-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Original Request Text</Label>
                  <Textarea
                    value={formData.original_request_text || ''}
                    onChange={(e) => handleChange('original_request_text', e.target.value)}
                    placeholder="Copy the original request text here (optional)..."
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(val) => handleChange('priority', val)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Assigned To</Label>
                    <Input
                      value={formData.assigned_to || ''}
                      onChange={(e) => handleChange('assigned_to', e.target.value)}
                      placeholder="Name of person handling this DSR"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 2: Requester */}
          <TabsContent value="requester" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Data Subject Information
                </CardTitle>
                <CardDescription>
                  Details of the person making the request
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name <span className="text-red-500">*</span></Label>
                    <Input
                      value={formData.requester.name}
                      onChange={(e) => handleNestedChange('requester', 'name', e.target.value)}
                      placeholder="Data subject's full name"
                      data-testid="requester-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email <span className="text-red-500">*</span></Label>
                    <Input
                      type="email"
                      value={formData.requester.email || ''}
                      onChange={(e) => handleNestedChange('requester', 'email', e.target.value)}
                      placeholder="Email address"
                      data-testid="requester-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.requester.phone || ''}
                      onChange={(e) => handleNestedChange('requester', 'phone', e.target.value)}
                      placeholder="Phone number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Relationship to Organisation</Label>
                    <Input
                      value={formData.requester.relationship || ''}
                      onChange={(e) => handleNestedChange('requester', 'relationship', e.target.value)}
                      placeholder="e.g., Employee, Customer, Former Employee"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Address</Label>
                  <Textarea
                    value={formData.requester.address || ''}
                    onChange={(e) => handleNestedChange('requester', 'address', e.target.value)}
                    placeholder="Postal address"
                    rows={2}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is-minor"
                    checked={formData.requester.is_minor}
                    onCheckedChange={(val) => handleNestedChange('requester', 'is_minor', val)}
                  />
                  <label htmlFor="is-minor" className="text-sm cursor-pointer">
                    Data subject is a minor (under 18)
                  </label>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Third Party / Agent Details
                </CardTitle>
                <CardDescription>
                  If someone is making this request on behalf of the data subject
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is-agent"
                    checked={formData.requester.is_agent}
                    onCheckedChange={(val) => handleNestedChange('requester', 'is_agent', val)}
                  />
                  <label htmlFor="is-agent" className="text-sm cursor-pointer">
                    Request is made by a third party / agent
                  </label>
                </div>

                {formData.requester.is_agent && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Agent Name</Label>
                        <Input
                          value={formData.requester.agent_name || ''}
                          onChange={(e) => handleNestedChange('requester', 'agent_name', e.target.value)}
                          placeholder="Name of the agent/representative"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Relationship to Data Subject</Label>
                        <Select
                          value={formData.requester.agent_relationship || "select"}
                          onValueChange={(val) => handleNestedChange('requester', 'agent_relationship', val === "select" ? "" : val)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select relationship" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="select">Select relationship</SelectItem>
                            {AGENT_RELATIONSHIPS.map(rel => (
                              <SelectItem key={rel.value} value={rel.value}>{rel.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="agent-auth"
                        checked={formData.requester.agent_authorization}
                        onCheckedChange={(val) => handleNestedChange('requester', 'agent_authorization', val)}
                      />
                      <label htmlFor="agent-auth" className="text-sm cursor-pointer">
                        Agent has provided authorization documentation
                      </label>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 3: Verification */}
          <TabsContent value="verification" className="space-y-6">
            {/* High-scrutiny reminders for minor or third-party */}
            {(isMinor || isThirdParty) && (
              <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
                <CardContent className="pt-6">
                  {isMinor && (
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <strong>Important:</strong> For a minor, identity verification must be conducted with the parent or legal guardian. The ID document and any authorisation belongs to the guardian, not the minor.
                    </p>
                  )}
                  {isThirdParty && (
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                      <strong>Important:</strong> This request is submitted by a third party. Written authorisation from the data subject is required before any data is disclosed.
                    </p>
                  )}
                  {isMinor && isThirdParty && (
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                      Both flags apply. This is a high-scrutiny request. Confirm both the guardian identity and the authorisation chain before proceeding.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  Identity Verification
                </CardTitle>
                <CardDescription>
                  Confirm the identity of the data subject before processing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.requester?.is_minor && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg mb-2">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      For a minor, identity verification must be conducted with the parent or legal guardian. The ID document and any authorisation belongs to the guardian, not the minor.
                    </p>
                  </div>
                )}
                <div className="p-4 bg-muted rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Identity Verified?</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={formData.identity_verification.verified === true ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleNestedChange('identity_verification', 'verified', true)}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Yes
                      </Button>
                      <Button
                        type="button"
                        variant={formData.identity_verification.verified === false ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleNestedChange('identity_verification', 'verified', false)}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        No
                      </Button>
                    </div>
                  </div>

                  {formData.identity_verification.verified && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                      <div className="space-y-2">
                        <Label>Verification Date</Label>
                        <CalendarInput
                          value={formData.identity_verification.verification_date?.split('T')[0] || ''}
                          onChange={(date) => handleNestedChange('identity_verification', 'verification_date', date)}
                          placeholder="Select verification date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Verification Method</Label>
                        <Select
                          value={formData.identity_verification.verification_method || "select"}
                          onValueChange={(val) => handleNestedChange('identity_verification', 'verification_method', val === "select" ? "" : val)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select method" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="select">Select method</SelectItem>
                            {VERIFICATION_METHODS.map(m => (
                              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {formData.identity_verification.verification_method && VERIFICATION_GUIDANCE[formData.identity_verification.verification_method] && (
                          <p className="text-xs text-slate-600 dark:text-slate-400 mt-2 p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded border-l-2 border-slate-300 dark:border-slate-600">
                            {VERIFICATION_GUIDANCE[formData.identity_verification.verification_method]}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Verification Notes</Label>
                        <Textarea
                          value={formData.identity_verification.verification_notes || ''}
                          onChange={(e) => handleNestedChange('identity_verification', 'verification_notes', e.target.value)}
                          placeholder="Additional notes about verification..."
                          rows={2}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="add-info-req"
                      checked={formData.identity_verification.additional_info_requested}
                      onCheckedChange={(val) => handleNestedChange('identity_verification', 'additional_info_requested', val)}
                    />
                    <label htmlFor="add-info-req" className="text-sm cursor-pointer">
                      Additional information requested from data subject
                    </label>
                  </div>

                  {formData.identity_verification.additional_info_requested && (
                    <div className="space-y-4 pl-6">
                      <div className="space-y-2">
                        <Label>Details of Information Requested</Label>
                        <Textarea
                          value={formData.identity_verification.additional_info_details || ''}
                          onChange={(e) => handleNestedChange('identity_verification', 'additional_info_details', e.target.value)}
                          placeholder="What additional information was requested..."
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Date Information Received</Label>
                        <CalendarInput
                          value={formData.identity_verification.additional_info_received_date?.split('T')[0] || ''}
                          onChange={(date) => handleNestedChange('identity_verification', 'additional_info_received_date', date)}
                          placeholder="Select date received"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {formData.requester?.is_agent && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    Third-Party Verification Requirements
                  </CardTitle>
                  <CardDescription>
                    Confirm identity verification and authorisation for third-party requests
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="third-party-verified"
                        checked={formData.identity_verification?.third_party_identity_verified || false}
                        onCheckedChange={(val) => handleNestedChange('identity_verification', 'third_party_identity_verified', val)}
                      />
                      <label htmlFor="third-party-verified" className="text-sm cursor-pointer">
                        Third party identity verified using the method above
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="agent-authorization"
                        checked={formData.requester?.agent_authorization || false}
                        onCheckedChange={(val) => handleNestedChange('requester', 'agent_authorization', val)}
                      />
                      <label htmlFor="agent-authorization" className="text-sm cursor-pointer">
                        Written authorisation from data subject obtained
                      </label>
                    </div>
                  </div>

                  {formData.identity_verification?.verified && !formData.requester?.agent_authorization && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        <strong>⚠️ Warning:</strong> The authorisation field has not been confirmed. Releasing data without verified written authorisation from the data subject may constitute a data breach.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Timer className="h-5 w-5 text-primary" />
                  Deadline Extension
                </CardTitle>
                <CardDescription>
                  If an extension to the deadline is needed
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="extension"
                    checked={formData.deadline_info.extension_applied}
                    onCheckedChange={(val) => handleNestedChange('deadline_info', 'extension_applied', val)}
                  />
                  <label htmlFor="extension" className="text-sm cursor-pointer">
                    Extension to deadline required
                  </label>
                </div>

                {formData.deadline_info.extension_applied && (
                  <>
                    {/* Legal obligation alert */}
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        <strong>Extension Applied —</strong> Extending the deadline triggers a legal obligation. You must notify the data subject of the extension and the reason within the original deadline period. This notification must be sent before the original deadline expires — not after. Record the date this notification was sent in the activity log.
                      </p>
                    </div>

                    <div className="space-y-4 pt-4 border-t">
                      <div className="space-y-2">
                        <Label>Reason for Extension</Label>
                        <Select
                          value={formData.deadline_info.extension_reason || "select"}
                          onValueChange={(val) => handleNestedChange('deadline_info', 'extension_reason', val === "select" ? "" : val)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select reason" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="select">Select reason</SelectItem>
                            <SelectItem value="complexity">Complexity of Request</SelectItem>
                            <SelectItem value="volume">Volume of Data</SelectItem>
                            <SelectItem value="multiple_requests">Multiple Requests</SelectItem>
                            <SelectItem value="third_party_consultation">Third Party Consultation</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Extended Deadline Date</Label>
                        <CalendarInput
                          value={formData.deadline_info.extended_deadline?.split('T')[0] || ''}
                          onChange={(date) => handleNestedChange('deadline_info', 'extended_deadline', date)}
                          placeholder="Select extended deadline"
                        />
                      </div>

                      {/* Extension notification fields */}
                      <div className="space-y-4 pt-4 border-t">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="extension-notified"
                            checked={formData.deadline_info.extension_notification_sent}
                            onCheckedChange={(val) => handleExtensionNotificationChange(val)}
                          />
                          <label htmlFor="extension-notified" className="text-sm cursor-pointer">
                            Extension notification sent to data subject
                          </label>
                        </div>
                        {formData.deadline_info.extension_notification_sent && (
                          <div className="space-y-2">
                            <Label>Date Extension Notification Sent</Label>
                            <CalendarInput
                              value={formData.deadline_info.extension_notification_date?.split('T')[0] || ''}
                              onChange={(date) => handleNestedChange('deadline_info', 'extension_notification_date', date)}
                              placeholder="Select notification date"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 4: Data Search */}
          <TabsContent value="data-search" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5 text-primary" />
                  Data Search & Collection
                </CardTitle>
                <CardDescription>
                  Document the search for the data subject's personal data
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <Label className="text-base">Data Search Completed?</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={formData.data_search.completed === true ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleNestedChange('data_search', 'completed', true)}
                    >
                      Yes
                    </Button>
                    <Button
                      type="button"
                      variant={formData.data_search.completed === false ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleNestedChange('data_search', 'completed', false)}
                    >
                      No
                    </Button>
                  </div>
                </div>

                {formData.data_search.completed && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Search Completed Date</Label>
                        <CalendarInput
                          value={formData.data_search.completed_date?.split('T')[0] || ''}
                          onChange={(date) => handleNestedChange('data_search', 'completed_date', date)}
                          placeholder="Select completion date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Personal Data Found?</Label>
                        <Select
                          value={formData.data_search.data_found === null ? "select" : String(formData.data_search.data_found)}
                          onValueChange={(val) => handleNestedChange('data_search', 'data_found', val === "select" ? null : val === "true")}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="select">Select</SelectItem>
                            <SelectItem value="true">Yes - Data Found</SelectItem>
                            <SelectItem value="false">No - No Data Found</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Systems / Departments Searched</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {DEPARTMENTS.map(dept => (
                          <div key={dept.value} className="flex items-center space-x-2">
                            <Checkbox
                              id={`dept-${dept.value}`}
                              checked={(formData.data_search.systems_searched || []).includes(dept.value)}
                              onCheckedChange={() => toggleArrayItem('data_search', 'systems_searched', dept.value)}
                            />
                            <label htmlFor={`dept-${dept.value}`} className="text-sm cursor-pointer">
                              {dept.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="third-party-data"
                        checked={formData.data_search.third_party_data_held}
                        onCheckedChange={(val) => handleNestedChange('data_search', 'third_party_data_held', val)}
                      />
                      <label htmlFor="third-party-data" className="text-sm cursor-pointer">
                        Third party data is held (may need redaction)
                      </label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="third-party-consult"
                        checked={formData.data_search.third_party_consultation_required}
                        onCheckedChange={(val) => handleNestedChange('data_search', 'third_party_consultation_required', val)}
                      />
                      <label htmlFor="third-party-consult" className="text-sm cursor-pointer">
                        Third party consultation required
                      </label>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 5: Response */}
          <TabsContent value="response" className="space-y-6">
            {/* High-scrutiny reminders for minor or third-party */}
            {(isMinor || isThirdParty) && (
              <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
                <CardContent className="pt-6">
                  {isMinor && (
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <strong>Important:</strong> For a minor, verification and response must be conducted with the parent or legal guardian.
                    </p>
                  )}
                  {isThirdParty && (
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                      <strong>Important:</strong> This request is submitted by a third party. Written authorisation from the data subject is required before any data is disclosed.
                    </p>
                  )}
                  {isMinor && isThirdParty && (
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                      Both flags apply. This is a high-scrutiny request. Confirm both the guardian identity and the authorisation chain before proceeding.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-primary" />
                  Decision & Response
                </CardTitle>
                <CardDescription>
                  Document the decision and response to the data subject
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.response_info.decision && (formData.response_info.decision.startsWith('refused_') || formData.response_info.decision === 'granted_partial') && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-700 dark:text-red-300 font-medium">Refusal Obligation</p>
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                      When refusing or partially granting a request, you must inform the data subject of: (1) the reason for the refusal, (2) their right to complain to the supervisory authority, and (3) their right to seek a judicial remedy. Confirm these have been communicated before closing this request.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Decision</Label>
                    <Select
                      value={formData.response_info.decision || "select"}
                      onValueChange={(val) => handleNestedChange('response_info', 'decision', val === "select" ? "" : val)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select decision" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="select">Select decision</SelectItem>
                        {DECISION_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Decision Date</Label>
                    <CalendarInput
                      value={formData.response_info.decision_date?.split('T')[0] || ''}
                      onChange={(date) => handleNestedChange('response_info', 'decision_date', date)}
                      placeholder="DD/MM/YYYY"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Decision Rationale</Label>
                  <Textarea
                    value={formData.response_info.decision_rationale || ''}
                    onChange={(e) => handleNestedChange('response_info', 'decision_rationale', e.target.value)}
                    placeholder="Explain the reasoning behind this decision..."
                    rows={3}
                  />
                </div>

                {/* Exemptions */}
                <div className="pt-4 border-t space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="exemption"
                      checked={formData.response_info.exemption_applies}
                      onCheckedChange={(val) => handleNestedChange('response_info', 'exemption_applies', val)}
                    />
                    <label htmlFor="exemption" className="text-sm cursor-pointer">
                      Exemption applies to this request
                    </label>
                  </div>

                  {formData.response_info.exemption_applies && (
                    <div className="space-y-4 pl-6">
                      <div className="space-y-3">
                        <Label>Exemption Types</Label>
                        <div className="space-y-2">
                          {EXEMPTION_TYPES.map(ex => (
                            <div key={ex.value} className="space-y-1">
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id={`ex-${ex.value}`}
                                  checked={(formData.response_info.exemption_types || []).includes(ex.value)}
                                  onCheckedChange={() => toggleArrayItem('response_info', 'exemption_types', ex.value)}
                                />
                                <label htmlFor={`ex-${ex.value}`} className="text-sm cursor-pointer">
                                  {ex.label}
                                </label>
                              </div>
                              {(formData.response_info.exemption_types || []).includes(ex.value) && EXEMPTION_GUIDANCE[ex.value] && (
                                <p className="text-xs text-slate-600 dark:text-slate-400 ml-6 p-2 bg-slate-50 dark:bg-slate-800/50 rounded border-l-2 border-slate-300 dark:border-slate-600">
                                  {EXEMPTION_GUIDANCE[ex.value]}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Exemption Justification</Label>
                        <Textarea
                          value={formData.response_info.exemption_justification || ''}
                          onChange={(e) => handleNestedChange('response_info', 'exemption_justification', e.target.value)}
                          placeholder="Justify the application of exemption(s)..."
                          rows={2}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Response Details */}
                <div className="pt-4 border-t space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Response Date</Label>
                      <CalendarInput
                        value={formData.response_info.response_date?.split('T')[0] || ''}
                        onChange={(date) => handleNestedChange('response_info', 'response_date', date)}
                        placeholder="Select response date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Response Method</Label>
                      <Select
                        value={formData.response_info.response_method || "select"}
                        onValueChange={(val) => handleNestedChange('response_info', 'response_method', val === "select" ? "" : val)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="select">Select method</SelectItem>
                          {RESPONSE_METHODS.map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Summary of Data Provided</Label>
                    <Textarea
                      value={formData.response_info.data_provided_summary || ''}
                      onChange={(e) => handleNestedChange('response_info', 'data_provided_summary', e.target.value)}
                      placeholder="Summarize what data was provided to the data subject..."
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="redactions"
                        checked={formData.response_info.redactions_applied}
                        onCheckedChange={(val) => handleNestedChange('response_info', 'redactions_applied', val)}
                      />
                      <label htmlFor="redactions" className="text-sm cursor-pointer">
                        Redactions applied
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="appeal-rights"
                        checked={formData.response_info.appeal_rights_communicated}
                        onCheckedChange={(val) => handleNestedChange('response_info', 'appeal_rights_communicated', val)}
                      />
                      <label htmlFor="appeal-rights" className="text-sm cursor-pointer font-medium">
                        I confirm that appeal and complaint rights have been communicated to the data subject in the response
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="regulator-contact"
                        checked={formData.response_info.regulator_contact_provided}
                        onCheckedChange={(val) => handleNestedChange('response_info', 'regulator_contact_provided', val)}
                      />
                      <label htmlFor="regulator-contact" className="text-sm cursor-pointer">
                        Regulator contact provided
                      </label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 6: Review */}
          <TabsContent value="review" className="space-y-6">
            {recordId && (
              <TasksSection sourceModule="dsr" sourceId={recordId} clientId={formData.client_id} entityId={formData.entity_id} title="Linked tasks" />
            )}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5 text-primary" />
                  Review & Finalise
                </CardTitle>
                <CardDescription>
                  Review all details before finalising
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Progress */}
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium">Completion Progress</h4>
                    <span className={cn(
                      "text-lg font-bold",
                      progressPercentage === 100 ? "text-green-500" : "text-primary"
                    )}>
                      {progressPercentage}%
                    </span>
                  </div>
                  <Progress value={progressPercentage} className="h-3" />
                </div>

                {/* Summary */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground">Reference</p>
                      <p className="font-mono font-medium">{dsrReference || 'Not yet assigned'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Request Type</p>
                      <Badge variant="outline">
                        {REQUEST_TYPES.find(t => t.value === formData.request_type)?.label || formData.request_type}
                      </Badge>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground">Requester</p>
                      <p className="font-medium">{formData.requester.name || '-'}</p>
                      <p className="text-sm text-muted-foreground">{formData.requester.email || ''}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Date Received</p>
                      <p className="font-medium">
                        {formData.date_received ? format(new Date(formData.date_received), 'PPP') : '-'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 border rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Identity Verified</p>
                      <Badge variant={formData.identity_verification.verified ? "success" : "secondary"}>
                        {formData.identity_verification.verified ? 'Yes' : 'No'}
                      </Badge>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Data Search</p>
                      <Badge variant={formData.data_search.completed ? "success" : "secondary"}>
                        {formData.data_search.completed ? 'Complete' : 'Pending'}
                      </Badge>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Decision</p>
                      <Badge variant={formData.response_info.decision ? "success" : "secondary"}>
                        {formData.response_info.decision 
                          ? DECISION_OPTIONS.find(d => d.value === formData.response_info.decision)?.label || formData.response_info.decision
                          : 'Pending'
                        }
                      </Badge>
                    </div>
                  </div>

                  {formData.request_summary && (
                    <div className="p-4 border rounded-lg">
                      <p className="text-xs text-muted-foreground mb-2">Request Summary</p>
                      <p className="text-sm">{formData.request_summary}</p>
                    </div>
                  )}
                </div>

                {/* Status */}
                <div className="space-y-2 pt-4 border-t">
                  <Label>Request Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(val) => handleChange('status', val)}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {(formData.response_info.decision?.startsWith('refused_') || formData.response_info.decision === 'granted_partial') &&
                   (formData.status === 'refused' || formData.status === 'closed') &&
                   !formData.response_info.appeal_rights_communicated && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-700 dark:text-red-300">
                        <strong>Cannot close or mark as refused</strong> without confirming appeal rights have been communicated. Return to <strong>Step 5 — Response</strong> to confirm.
                      </p>
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div className="space-y-2 pt-4 border-t">
                  <Label>Additional Notes</Label>
                  <Textarea
                    value={formData.notes || ''}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Any additional notes about this DSR..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              variant="outline"
              onClick={goToPrevTab}
              disabled={activeTab === TABS[0].id}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/compliance/dsr')}>
                Cancel
              </Button>
              {recordId && (
                <Button variant="outline" onClick={async () => {
                  try {
                    const res = await exportAPI.dsrDocx(recordId);
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a'); a.href = url;
                    a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'DSR_Report.docx';
                    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                    toast.success('Exported');
                  } catch { toast.error('Export failed'); }
                }} data-testid="export-dsr-docx"><FileDown className="h-4 w-4 mr-2" />Export DOCX</Button>
              )}
              {!isReadOnly && (
                <Button variant="outline" onClick={() => handleSave(true)} disabled={saving}>
                  Save as Draft
                </Button>
              )}
              {activeTab === 'review' ? (
                !isReadOnly && (
                  <Button onClick={() => handleSave(false)} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save & Close'}
                  </Button>
                )
              ) : (
                <Button onClick={goToNextTab}>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </Tabs>
        </div>
      </div>
    </div>
    </>
  );
}
